import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, UserPlus2, Loader2, Upload, X, FileText, Plus, Briefcase } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { api } from "../../utils/api";
import { toast } from "sonner";

interface Requisition {
  id: number;
  jobRole: { roleName: string } | null;
}

const AddCandidate: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [req, setReq] = useState<Requisition | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    currentPosition: "",
    currentCompany: "",
    source: "Website",
    yearsExperience: "0",
    linkedInProfile: "",
  });

  const [attachments, setAttachments] = useState<File[]>([]);
  const [uploadingAttachments, setUploadingAttachments] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  interface ExperienceForm {
    roleTitle: string;
    companyName: string;
    startDate: string;
    endDate: string;
    responsibilities: string;
  }

  const [experiences, setExperiences] = useState<ExperienceForm[]>([]);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    api.get<{ success: boolean; requisition: Requisition }>(`/api/requisitions/${id}`)
      .then((res) => {
        if (res.success) setReq(res.requisition);
      })
      .catch(() => {
        toast.error("Failed to load requisition details");
      })
      .finally(() => setLoading(false));
  }, [id]);

  const handleChange = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const newFiles = Array.from(e.target.files) as File[];
    setAttachments((prev) => [...prev, ...newFiles]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const addExperience = () => {
    setExperiences((prev) => [
      ...prev,
      { roleTitle: "", companyName: "", startDate: "", endDate: "", responsibilities: "" },
    ]);
  };

  const updateExperience = (index: number, field: keyof ExperienceForm, value: string) => {
    setExperiences((prev) =>
      prev.map((exp, i) => (i === index ? { ...exp, [field]: value } : exp))
    );
  };

  const removeExperience = (index: number) => {
    setExperiences((prev) => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleSubmit = async () => {
    if (!id) return;
    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim()) {
      toast.error("First name, last name and email are required");
      return;
    }
    setSubmitting(true);
    let candidateId: number | null = null;
    try {
      const payload = {
        requisitionId: Number(id),
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        phone: form.phone || null,
        currentPosition: form.currentPosition || null,
        currentCompany: form.currentCompany || null,
        source: form.source,
        yearsExperience: form.yearsExperience ? parseInt(form.yearsExperience) : null,
        linkedInProfile: form.linkedInProfile || null,
      };
      const res = await api.post<{ success: boolean; candidate: { id: number } }>("/api/candidates", payload);
      if (res.success && res.candidate) {
        candidateId = res.candidate.id;

        // Upload attachments if any
        if (attachments.length > 0) {
          setUploadingAttachments(true);
          let uploadedCount = 0;
          let failedCount = 0;

          for (const file of attachments) {
            try {
              const formData = new FormData();
              formData.append("file", file);
              await api.post(`/api/candidates/${candidateId}/attachments`, formData);
              uploadedCount++;
            } catch {
              failedCount++;
            }
          }

          if (uploadedCount > 0) {
            toast.success(`${uploadedCount} document${uploadedCount > 1 ? "s" : ""} uploaded`);
          }
          if (failedCount > 0) {
            toast.error(`Failed to upload ${failedCount} document${failedCount > 1 ? "s" : ""}`);
          }
          setUploadingAttachments(false);
        }

        // Create experiences if any
        const validExperiences = experiences.filter((e) => e.roleTitle.trim());
        if (validExperiences.length > 0 && candidateId) {
          let expCreated = 0;
          let expFailed = 0;
          for (const exp of validExperiences) {
            try {
              await api.post(`/api/candidates/${candidateId}/experiences`, {
                roleTitle: exp.roleTitle.trim(),
                companyName: exp.companyName.trim() || null,
                startDate: exp.startDate || null,
                endDate: exp.endDate || null,
                responsibilities: exp.responsibilities.trim() || null,
              });
              expCreated++;
            } catch {
              expFailed++;
            }
          }
          if (expFailed > 0) {
            toast.error(`Failed to create ${expFailed} experience${expFailed > 1 ? "s" : ""}`);
          }
        }

        toast.success("Candidate added successfully");
        navigate(`/recruitment/requisitions/${id}`);
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to add candidate");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 animate-in fade-in duration-500">
        <Loader2 size={32} className="animate-spin text-gray-400" />
        <p className="mt-3 text-sm font-medium text-gray-500">Loading requisition...</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto animate-in fade-in duration-500 pb-10 px-4 lg:px-0">
      {/* Back Button */}
      <button
        onClick={() => navigate(`/recruitment/requisitions/${id}`)}
        className="flex items-center gap-2 text-gray-500 hover:text-gray-700 transition-colors w-fit mb-6"
      >
        <ChevronLeft size={18} />
        <span className="text-sm font-medium">Back to Requisition</span>
      </button>

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3">
          <UserPlus2 size={28} className="text-gray-900" />
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
            Add New Candidate
          </h1>
        </div>
        <p className="text-sm text-gray-500 font-medium mt-1">
          Add a candidate for the {req?.jobRole?.roleName ?? "Unnamed"} position
        </p>
      </div>

      {/* Form */}
      <div className="bg-white p-6 sm:p-8 rounded-3xl border border-gray-100 shadow-sm space-y-6">
        {/* Row 1: First Name + Last Name */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className="text-sm font-bold text-gray-900">First Name</label>
            <Input
              className="bg-gray-50 border-gray-100 rounded-lg h-10 text-sm font-medium focus:ring-2 focus:ring-blue-100"
              value={form.firstName}
              onChange={(e) => handleChange("firstName", e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-bold text-gray-900">Last Name</label>
            <Input
              className="bg-gray-50 border-gray-100 rounded-lg h-10 text-sm font-medium focus:ring-2 focus:ring-blue-100"
              value={form.lastName}
              onChange={(e) => handleChange("lastName", e.target.value)}
            />
          </div>
        </div>

        {/* Row 2: Email + Phone */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className="text-sm font-bold text-gray-900">Email</label>
            <Input
              type="email"
              className="bg-gray-50 border-gray-100 rounded-lg h-10 text-sm font-medium focus:ring-2 focus:ring-blue-100"
              value={form.email}
              onChange={(e) => handleChange("email", e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-bold text-gray-900">Phone</label>
            <Input
              type="tel"
              className="bg-gray-50 border-gray-100 rounded-lg h-10 text-sm font-medium focus:ring-2 focus:ring-blue-100"
              value={form.phone}
              onChange={(e) => handleChange("phone", e.target.value)}
            />
          </div>
        </div>

        {/* Row 3: Current Position + Current Company */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className="text-sm font-bold text-gray-900">Current Position</label>
            <Input
              className="bg-gray-50 border-gray-100 rounded-lg h-10 text-sm font-medium focus:ring-2 focus:ring-blue-100"
              value={form.currentPosition}
              onChange={(e) => handleChange("currentPosition", e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-bold text-gray-900">Current Company</label>
            <Input
              className="bg-gray-50 border-gray-100 rounded-lg h-10 text-sm font-medium focus:ring-2 focus:ring-blue-100"
              value={form.currentCompany}
              onChange={(e) => handleChange("currentCompany", e.target.value)}
            />
          </div>
        </div>

        {/* Row 4: Source + Years Experience */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className="text-sm font-bold text-gray-900">Source</label>
            <Select value={form.source} onValueChange={(val) => handleChange("source", val)}>
              <SelectTrigger className="bg-gray-50 border-gray-100 rounded-lg h-10 text-sm font-medium focus:ring-2 focus:ring-blue-100">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                {["Website", "LinkedIn", "Referral", "Indeed", "Seek"].map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-bold text-gray-900">Years Experience</label>
            <Input
              type="number"
              className="bg-gray-50 border-gray-100 rounded-lg h-10 text-sm font-medium focus:ring-2 focus:ring-blue-100"
              value={form.yearsExperience}
              onChange={(e) => handleChange("yearsExperience", e.target.value)}
            />
          </div>
        </div>

        {/* Row 5: LinkedIn Profile */}
        <div className="space-y-1.5">
          <label className="text-sm font-bold text-gray-900">LinkedIn Profile (optional)</label>
          <Input
            className="bg-gray-50 border-gray-100 rounded-lg h-10 text-sm font-medium focus:ring-2 focus:ring-blue-100"
            value={form.linkedInProfile}
            onChange={(e) => handleChange("linkedInProfile", e.target.value)}
          />
        </div>

        {/* Past Experience */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-sm font-bold text-gray-900">Past Experience</label>
            <button
              type="button"
              onClick={addExperience}
              className="flex items-center gap-1 text-xs font-bold text-blue-600 hover:text-blue-700 transition-colors"
            >
              <Plus size={14} />
              <span>Add Experience</span>
            </button>
          </div>

          {experiences.length === 0 ? (
            <p className="text-sm text-gray-500 font-medium">No past experience added yet.</p>
          ) : (
            <div className="space-y-4">
              {experiences.map((exp, idx) => (
                <div key={idx} className="p-4 rounded-xl border border-gray-100 bg-gray-50/50 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-gray-400">
                      <Briefcase size={14} />
                      <span className="text-xs font-bold uppercase tracking-widest">Experience {idx + 1}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeExperience(idx)}
                      className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                    >
                      <X size={14} />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-gray-700">Role Title</label>
                      <Input
                        placeholder="e.g. Senior Engineer"
                        className="bg-white border-gray-200 rounded-lg h-9 text-sm font-medium"
                        value={exp.roleTitle}
                        onChange={(e) => updateExperience(idx, "roleTitle", e.target.value)}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-gray-700">Company Name</label>
                      <Input
                        placeholder="e.g. Acme Corp"
                        className="bg-white border-gray-200 rounded-lg h-9 text-sm font-medium"
                        value={exp.companyName}
                        onChange={(e) => updateExperience(idx, "companyName", e.target.value)}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-gray-700">Start Date</label>
                      <Input
                        type="date"
                        className="bg-white border-gray-200 rounded-lg h-9 text-sm font-medium"
                        value={exp.startDate}
                        onChange={(e) => updateExperience(idx, "startDate", e.target.value)}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-gray-700">End Date</label>
                      <Input
                        type="date"
                        className="bg-white border-gray-200 rounded-lg h-9 text-sm font-medium"
                        value={exp.endDate}
                        onChange={(e) => updateExperience(idx, "endDate", e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-gray-700">Responsibilities</label>
                    <textarea
                      placeholder="Describe key responsibilities..."
                      className="w-full bg-white border border-gray-200 rounded-lg p-3 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-100 min-h-[80px] resize-y"
                      value={exp.responsibilities}
                      onChange={(e) => updateExperience(idx, "responsibilities", e.target.value)}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Attachments */}
        <div className="space-y-3">
          <label className="text-sm font-bold text-gray-900">Attachments</label>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleFileChange}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-dashed border-gray-300 text-sm font-medium text-gray-600 hover:border-gray-400 hover:bg-gray-50 transition-colors w-full sm:w-fit"
          >
            <Upload size={16} />
            <span>Upload documents</span>
          </button>

          {attachments.length > 0 && (
            <div className="space-y-2">
              {attachments.map((file, idx) => (
                <div
                  key={`${file.name}-${idx}`}
                  className="flex items-center justify-between p-3 rounded-xl bg-gray-50 border border-gray-100"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText size={18} className="text-gray-400 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                      <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeAttachment(idx)}
                    className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors shrink-0"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
          <Button
            variant="outline"
            className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-10 rounded-xl text-sm font-bold px-5"
            onClick={() => navigate(`/recruitment/requisitions/${id}`)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            className="bg-slate-950 hover:bg-slate-900 text-white h-10 rounded-xl text-sm font-bold px-5"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting || uploadingAttachments ? (
              <>
                <Loader2 size={16} className="animate-spin mr-2" />
                {uploadingAttachments ? "Uploading documents..." : "Saving..."}
              </>
            ) : (
              "Add Candidate"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddCandidate;
